/**
 * Vorlage „Mit der Gabel einstechen“.
 *
 * Der Teigboden liegt in einer flachen Form. Eine Gabel sticht an mehreren
 * Stellen ein; zurück bleiben kleine Lochreihen, damit der Boden beim Backen
 * keine Blasen wirft.
 */
import { ease, lerp, segment } from '../geometry.ts';
import type { Point } from '../geometry.ts';
import { compact, ellipse, faded, group, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { arrow, counter, GROUND_Y, shadow } from '../parts/stage.ts';
import { fork, place } from '../parts/utensils.ts';
import { cakeTin, contents } from '../parts/vessels.ts';
import { defineTemplate } from '../template.ts';

/** Flache Tarteform. */
const TIN = { cx: 112, width: 168, height: 14 } as const;
const TIN_RY = TIN.width * 0.16;
const RIM_Y = GROUND_Y - TIN.height - TIN_RY;
const LEVEL = 0.4;
/** Höhe, aus der die Gabel einsticht. */
const LIFT = 24;

/** Einstichstellen, relativ zur Mitte der Teigoberfläche. */
const POKES: readonly Point[] = [
  { x: -48, y: -6 },
  { x: -14, y: -10 },
  { x: 22, y: -8 },
  { x: 52, y: 0 },
  { x: 14, y: 6 },
  { x: -26, y: 7 },
];

/** Zeitplan. */
const TIME = { appear: [0.02, 0.08], pokes: [0.08, 0.8], vanish: [0.8, 0.86], reset: [0.88, 1] } as const;

/** Reihe aus vier Löchern, wie sie die Zinken hinterlassen. */
function holes(at: Point): SceneNode[] {
  return [-4.5, -1.5, 1.5, 4.5].map((dx) => ellipse(at.x + dx * 1.1, at.y, 1.4, 1, styles.flat('cavity')));
}

/** Vorlage „Mit der Gabel einstechen“. */
export const bakePrickTemplate = defineTemplate({
  id: 'bake.prick',
  group: 'bake',
  title: 'Mit der Gabel einstechen',
  description:
    'Der Teigboden wird mit einer Gabel mehrmals eingestochen. So entweicht beim Backen der Dampf und der Boden bleibt flach.',
  durationSeconds: 5,
  keywords: [
    'einstechen',
    'mehrmals einstechen',
    'mit einer gabel einstechen',
    'mit der gabel einstechen',
    'löchern',
    'blind backen',
    'blindbacken',
    'boden einstechen',
  ],
  stillTime: 0.3,
  parameters: [],
  scene(_values, t) {
    const tin = cakeTin(TIN.cx, RIM_Y, TIN.width, TIN.height);
    const surfaceY = RIM_Y + tin.depth * LEVEL;
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const visible = ease.outCubic(segment(t, ...TIME.appear)) * (1 - segment(t, ...TIME.vanish));

    // Jede Einstichstelle hat einen eigenen Abschnitt: hinfahren, einstechen, heben.
    const phase = segment(t, ...TIME.pokes) * POKES.length;
    const index = Math.min(POKES.length - 1, Math.floor(phase));
    const local = phase - index;
    const current = POKES[index] as Point;
    const previous = POKES[Math.max(0, index - 1)] as Point;
    const move = index === 0 ? 1 : ease.inOutCubic(segment(local, 0, 0.4));
    const dip = ease.inOutCubic(segment(local, 0.4, 0.62)) - ease.inOutCubic(segment(local, 0.7, 1));
    const x = TIN.cx + lerp(previous.x, current.x, move);
    const y = surfaceY + lerp(previous.y, current.y, move) - LIFT * (1 - dip);

    const done: SceneNode[] = [];
    POKES.forEach((point, pokeIndex) => {
      const madeAt = TIME.pokes[0] + ((pokeIndex + 0.6) / POKES.length) * (TIME.pokes[1] - TIME.pokes[0]);
      if (t >= madeAt) {
        done.push(...holes({ x: TIN.cx + point.x, y: surfaceY + point.y }));
      }
    });

    return compact([
      ...counter(),
      shadow(TIN.cx, TIN.width * 0.46),
      ...tin.back,
      contents(tin, LEVEL, 'tint', done.length > 0 ? [faded(1 - reset, done)] : []),
      ...tin.front,
      faded(visible, [group([place(x, y, 8, fork(), 0.8)])]),
    ]);
  },
  hints() {
    return [
      arrow([
        { x: TIN.cx + 34, y: RIM_Y - 46 },
        { x: TIN.cx + 34, y: RIM_Y - 12 },
      ]),
    ];
  },
});
